import {
  PDFCheckBox,
  PDFDict,
  PDFDocument,
  PDFHexString,
  PDFName,
  PDFString,
  PDFTextField,
  StandardFonts,
  rgb,
} from 'pdf-lib';
import type { PDFFont, PDFForm } from 'pdf-lib';

import { type Facts } from '@/james-playground/aa-family-claim/claim/facts';
import {
  BOXES,
  FONT_SIZE,
  layoutLines,
  widgetValues,
  type AaWidgetValues,
  type Box,
  type WidgetValue,
} from '@/james-playground/aa-family-claim/claim/pdf';

/**
 * Fills the official AA1 11/25 (Version 7.0) from the claim. What goes in
 * each box is decided by `widgetValues` in claim/pdf.ts, from the registry;
 * this file only writes those values into the template and, when Q63 runs
 * over, adds separate sheets. Signature spaces and their dates stay blank.
 */
export const AA_TEMPLATE_URL = '/aa/aa1-11-25.pdf';
export const AA_TEMPLATE_VERSION = '11/25 Version 7.0';

/** The claim's facts, or the widget values already worked out from them. */
export type AaPdfInput = Facts | AaWidgetValues;

const isWidgetValues = (input: object): input is AaWidgetValues =>
  'values' in input && Array.isArray((input as AaWidgetValues).continuation);

/** Whatever the caller has, as widget values. */
export const toWidgetValues = (
  input: AaPdfInput,
): AaWidgetValues => {
  if (isWidgetValues(input)) return input;
  return widgetValues(input);
};

/** Browser convenience API. The template is served from Vite public/. */
export async function fillOfficialAaPdf(
  input: AaPdfInput,
): Promise<Uint8Array> {
  const response = await fetch(AA_TEMPLATE_URL);
  if (!response.ok)
    throw new Error(
      `The Attendance Allowance template could not be loaded (${response.status}).`,
    );
  return fillOfficialAaPdfFromBytes(
    input,
    new Uint8Array(await response.arrayBuffer()),
  );
}

/** Testable pure entry point. Performs no network request and uploads no user data. */
export async function fillOfficialAaPdfFromBytes(
  input: AaPdfInput,
  template: Uint8Array,
): Promise<Uint8Array> {
  const widgets = toWidgetValues(input);
  const doc = await PDFDocument.load(template);
  if (doc.getPageCount() !== 32)
    throw new Error('Expected the AA1 11/25 32-page template.');
  const form = doc.getForm();
  assertBoxes(form);
  const font = await doc.embedFont(StandardFonts.Helvetica);

  // Text boxes are drawn in Helvetica (Arial's widths, so the official layout
  // holds) as they are written; tick boxes keep the template's appearances.
  for (const [name, value] of Object.entries(widgets.values)) {
    const field = form.getField(name);
    if (field instanceof PDFTextField) setText(field, name, value, font);
    else if (field instanceof PDFCheckBox) setTick(form, field, name, value);
    else throw new Error(`AA1 field ${name} is neither a text box nor a tick box.`);
  }
  addFontToForm(form, font);

  if (widgets.extraSheets.length) addExtraSheets(doc, font, widgets);
  doc.setTitle('Attendance Allowance claim form AA1');
  doc.setSubject(`AA1 ${AA_TEMPLATE_VERSION}`);
  return doc.save({ updateFieldAppearances: false });
}

// ---- Writing ---------------------------------------------------------------

/**
 * Write a text box and draw it.
 *
 * The template writes the "/" in every text box's DA as the octal escape
 * \057. pdf-lib reads the font size from the raw string, finds none, and
 * auto-sizes the text to fill the box, so the DA is stored decoded to keep
 * the form's 10pt, the size claim/pdf.ts measured against.
 *
 * A multiline box is drawn from the lines claim/pdf.ts laid out (pdf-lib's
 * own breaks run up to 1pt past the box edge; see `widthOf`), then its value
 * is set back to the family's words, so /V carries no inserted line breaks.
 * Comb boxes keep their MaxLen; pdf-lib refuses longer text.
 */
function setText(
  field: PDFTextField,
  name: string,
  value: WidgetValue,
  font: PDFFont,
) {
  if (typeof value !== 'string')
    throw new Error(`AA1 text box ${name} was given ${String(value)}.`);
  const da = field.acroField.dict.lookup(PDFName.of('DA'));
  if (da instanceof PDFString || da instanceof PDFHexString)
    field.acroField.setDefaultAppearance(da.decodeText());
  const box = BOXES[name];
  const drawn =
    box?.[2] === 'm' ? layoutLines(value, box[0]).lines.join('\n') : value;
  field.setText(drawn);
  field.updateAppearances(font);
  if (drawn !== value) field.acroField.setValue(PDFHexString.fromText(value));
}

/** Viewers that redraw a box on editing look its DA font up in the form's DR. */
function addFontToForm(form: PDFForm, font: PDFFont) {
  const dr = form.acroForm.dict.lookup(PDFName.of('DR'), PDFDict);
  dr.lookup(PDFName.of('Font'), PDFDict).set(PDFName.of(font.name), font.ref);
}

/**
 * DWP encodes every choice as a checkbox field (no radio flag). A field with
 * several kids is one exclusive group whose kids carry different on-states, so
 * the field's /V takes the chosen state and each kid's /AS is that state or
 * Off. pdf-lib's check() would pick the first on-state, which is wrong here.
 * `true` ticks a box with a single on-state.
 */
function setTick(
  form: PDFForm,
  field: PDFCheckBox,
  name: string,
  value: WidgetValue,
) {
  const kids = field.acroField.getWidgets();
  const states = kids.map((kid) => kid.getOnValue());
  const decoded = states.map((s) => s?.decodeText());
  if (value === false) return;
  const wanted =
    value === true
      ? new Set(decoded).size === 1
        ? decoded[0]
        : undefined
      : value;
  const on = states.find((s) => s && s.decodeText() === wanted);
  if (!on)
    throw new Error(
      `AA1 tick box ${name} has no on-state "${String(value)}" (it has ${decoded.join(', ')}).`,
    );
  field.acroField.dict.set(PDFName.of('V'), on);
  kids.forEach((kid, i) =>
    kid.setAppearanceState(states[i] === on ? on : PDFName.of('Off')),
  );
  form.markFieldAsClean(field.ref);
}

// ---- The template's boxes ---------------------------------------------------

/** Each text box as pdf-lib lays it out (see `Box` in claim/pdf.ts). */
export const templateBoxes = (form: PDFForm): Record<string, Box> => {
  const boxes: Record<string, Box> = {};
  for (const field of form.getFields()) {
    if (!(field instanceof PDFTextField)) continue;
    const [widget] = field.acroField.getWidgets();
    const rect = widget.getRectangle();
    const border = widget.getBorderStyle()?.getWidth() ?? 0;
    const inset = (border + (field.isCombed() ? 0 : 1)) * 2;
    boxes[field.getName()] = [
      Number((rect.width - inset).toFixed(2)),
      Number((rect.height - inset).toFixed(2)),
      field.isCombed()
        ? (field.getMaxLength() ?? 0)
        : field.isMultiline()
          ? 'm'
          : 's',
    ];
  }
  return boxes;
};

/** The fitting rules in claim/pdf.ts measured this template; refuse any other. */
function assertBoxes(form: PDFForm) {
  const actual = templateBoxes(form);
  for (const [name, [width, height, kind]] of Object.entries(BOXES)) {
    const box = actual[name];
    if (
      !box ||
      Math.abs(box[0] - width) > 0.01 ||
      Math.abs(box[1] - height) > 0.01 ||
      box[2] !== kind
    )
      throw new Error(
        `AA1 template box ${name} differs from BOXES in claim/pdf.ts; regenerate BOXES from templateBoxes().`,
      );
  }
}

// ---- Separate sheets for Q63 -------------------------------------------------

const A4: [number, number] = [595.276, 841.89];
const MARGIN = 56;

/**
 * The form's own instruction for Q63: continue on a separate piece of paper,
 * with the claimant's name and NI number at the top of each page.
 */
function addExtraSheets(doc: PDFDocument, font: PDFFont, w: AaWidgetValues) {
  const text = (name: string) => {
    const v = w.values[name];
    return typeof v === 'string' ? v : '';
  };
  const name = [text('01-01iii'), text('01-01i 2'), text('01-01i')]
    .filter(Boolean)
    .join(' ');
  const header = [
    'Attendance Allowance claim: question 63 continued',
    `Name: ${name || 'not given'}`,
    `National Insurance number: ${text('01-02NINO') || 'not given'}`,
  ];
  const step = font.heightAtSize(FONT_SIZE) * 1.2;
  const width = A4[0] - MARGIN * 2;
  const colour = rgb(0, 0, 0);
  let page = doc.addPage(A4);
  let y = 0;
  const startPage = () => {
    y = A4[1] - MARGIN;
    for (const line of header) {
      page.drawText(line, { x: MARGIN, y, size: FONT_SIZE, font, color: colour });
      y -= step;
    }
    y -= step;
  };
  startPage();
  for (const paragraph of w.extraSheets) {
    for (const line of layoutLines(paragraph, width).lines) {
      if (y < MARGIN) {
        page = doc.addPage(A4);
        startPage();
      }
      page.drawText(line, { x: MARGIN, y, size: FONT_SIZE, font, color: colour });
      y -= step;
    }
    y -= step;
  }
}
